import { useState } from "react";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const MOCK_ACADEMIES = [
  {
    id: 1, name: "Northside Cricket Club", coach: "James Martinez", contact: "info@northside.com",
    teams: [
      { ageGroup: "U11", playerCount: 14, status: "registered",
        players: ["Aryan S","Dev P","Kabir M","Rohan T","Aidan W","Neel K","Zain A","Kiran B","Dhruv R","Jay C","Om S","Raj L","Aarav N","Veer P"] },
      { ageGroup: "U13", playerCount: 16, status: "registered",
        players: ["Quinn F","Ansh S","Marcus J","Tyler R","Nikhil V","Jordan L","Sam P","Chris M","Ben H","Liam O","Noah W","Ethan K","Mason D","Logan F","Lucas G","Ryan B"] },
    ],
  },
  {
    id: 2, name: "Eastside Academy", coach: "Robert Thompson", contact: "info@eastside.com",
    teams: [
      { ageGroup: "U13", playerCount: 12, status: "registered",
        players: ["Daron W","Priya S","Amir K","Sai T","Nathan H","Connor M","Jake F","Tyler G","Blake A","Drew L","Cole B","Reed P"] },
      { ageGroup: "U15", playerCount: 10, status: "pending",
        players: ["Shane M","Brad T","Tyler W","Connor S","Dylan R","Austin P","Cody L","Trevor K","Brent H","Ross G"] },
    ],
  },
  {
    id: 3, name: "Westfield Cricket", coach: "Maria Davis", contact: "info@westfield.com",
    teams: [
      { ageGroup: "U11", playerCount: 11, status: "registered",
        players: ["Nirvaan P","Arun S","Keshav M","Tarun B","Vishal R","Pranav K","Rahul T","Suresh L","Vikram G","Arjun H","Mohan N"] },
      { ageGroup: "U13", playerCount: 13, status: "registered",
        players: ["Ravi S","Ajay P","Deepak M","Sunil T","Anil K","Vinod R","Ramesh B","Prakash G","Ganesh H","Mahesh L","Naresh N","Rajesh F","Suresh W"] },
    ],
  },
  {
    id: 4, name: "Central Sports Academy", coach: "Steve Wilson", contact: "info@central.com",
    teams: [
      { ageGroup: "U11", playerCount: 15, status: "registered",
        players: ["Sam A","Tom B","Jack C","Will D","Finn E","Leo F","Max G","Eli H","Kai I","Rex J","Ace K","Beau L","Cruz M","Dean N","Earl O"] },
      { ageGroup: "U13", playerCount: 14, status: "registered",
        players: ["Adi P","Bren Q","Carl R","Dan S","Ed T","Frank U","Greg V","Hank W","Ivan X","Joe Y","Ken Z","Lars A","Mike B","Nick C"] },
    ],
  },
];

const AGE_GROUPS = ["U11", "U13", "U15", "U17"] as const;
type AgeTab = (typeof AGE_GROUPS)[number];
type ActiveFilter = "all" | "teams" | "players" | "pending";

const STATUS_BADGE: Record<string, string> = {
  registered: "bg-green-100 text-green-700 border-green-300",
  pending:    "bg-amber-100 text-amber-700 border-amber-300",
};

/** Returns all teams for a given age group, annotated with academy info. */
function teamsForAge(ageGroup: AgeTab) {
  return MOCK_ACADEMIES.flatMap((a) =>
    a.teams
      .filter((t) => t.ageGroup === ageGroup)
      .map((t) => ({ ...t, academyName: a.name, coach: a.coach, academyId: a.id }))
  );
}

export default function HubTeamsRostersDemo() {
  const { toast } = useToast();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [ageTab, setAgeTab] = useState<AgeTab>("U11");
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<string | null>(null);

  function toggleTeam(key: string) {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function handleImport() {
    if (!uploadFile) return;
    toast({ title: "Roster imported", description: `${uploadFile} uploaded successfully.` });
    setTimeout(() => { setImportOpen(false); setUploadFile(null); }, 1000);
  }

  // Summary stats
  const totalAcademies = MOCK_ACADEMIES.length;
  const allTeams = MOCK_ACADEMIES.flatMap((a) => a.teams.map((t) => ({ ...t, academyName: a.name, coach: a.coach, academyId: a.id })));
  const totalTeams = allTeams.length;
  const totalPlayers = allTeams.reduce((s, t) => s + t.playerCount, 0);
  const pendingTeams = allTeams.filter((t) => t.status === "pending");
  const pendingCount = pendingTeams.length;

  const summaryTiles: { label: string; value: number; filter: ActiveFilter; warn?: boolean }[] = [
    { label: "Academies", value: totalAcademies, filter: "all" },
    { label: "Teams",     value: totalTeams,     filter: "teams" },
    { label: "Players",   value: totalPlayers,   filter: "players" },
    { label: "Pending",   value: pendingCount,   filter: "pending", warn: pendingCount > 0 },
  ];

  // Age-group tab counts
  const ageGroupCounts: Record<AgeTab, { teams: number; players: number }> = {
    U11: { teams: teamsForAge("U11").length, players: teamsForAge("U11").reduce((s, t) => s + t.playerCount, 0) },
    U13: { teams: teamsForAge("U13").length, players: teamsForAge("U13").reduce((s, t) => s + t.playerCount, 0) },
    U15: { teams: teamsForAge("U15").length, players: teamsForAge("U15").reduce((s, t) => s + t.playerCount, 0) },
    U17: { teams: 0, players: 0 },
  };

  /** Shared age-group-first roster view used by "all" and "players" sub-views */
  function AgeGroupRoster({ showPlayers }: { showPlayers: boolean }) {
    const teams = teamsForAge(ageTab);
    return (
      <div className="space-y-3" data-testid="age-group-roster">
        {teams.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-sm text-gray-400 italic">
            No teams registered for {ageTab} yet.
          </div>
        ) : (
          teams.map((team) => {
            const key = `${team.academyId}-${team.ageGroup}`;
            const isOpen = expandedTeams.has(key);
            return (
              <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`team-card-${key}`}>
                <button
                  className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => toggleTeam(key)}
                  data-testid={`btn-toggle-team-${key}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${team.status === "pending" ? "bg-amber-400" : "bg-green-500"}`} />
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{team.academyName}</p>
                      <p className="text-xs text-gray-500">Coach: {team.coach} · {team.playerCount} players</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`border text-xs ${STATUS_BADGE[team.status]}`}>{team.status}</Badge>
                    <span className="text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
                  </div>
                </button>

                {isOpen && showPlayers && (
                  <div className="border-t border-gray-100 px-5 py-4" data-testid={`player-chips-${key}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {team.players.map((name, idx) => (
                        <div key={name} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                          <span className="text-xs text-gray-400 font-mono w-4 shrink-0">{idx + 1}</span>
                          <span className="text-sm text-gray-800 truncate">{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isOpen && !showPlayers && (
                  <div className="border-t border-gray-100 px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{team.playerCount}</p>
                      <p className="text-xs text-gray-500">Players</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">1</p>
                      <p className="text-xs text-gray-500">Team</p>
                    </div>
                    <div className="text-center">
                      <Badge className={`border text-xs ${STATUS_BADGE[team.status]}`}>{team.status}</Badge>
                      <p className="text-xs text-gray-500 mt-1">Status</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  }

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-teams-rosters-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">Teams & Rosters</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
          </div>
          <Button
            size="sm"
            className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
            data-testid="btn-import-roster"
            onClick={() => setImportOpen(true)}
          >
            Import Roster
          </Button>
        </div>

        {/* Clickable summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-bar">
          {summaryTiles.map(({ label, value, filter, warn }) => (
            <button
              key={label}
              onClick={() => setActiveFilter(filter)}
              data-testid={`tile-${filter}`}
              className={`rounded-xl border px-4 py-3 text-center transition-all ${
                warn ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"
              } ${activeFilter === filter ? "ring-2 ring-[#1E2761]" : "hover:border-gray-300"}`}
            >
              <p className={`text-2xl font-bold ${warn ? "text-amber-700" : "text-gray-900"}`}>{value}</p>
              <p className={`text-xs mt-0.5 ${warn ? "text-amber-600" : "text-gray-500"}`}>{label}</p>
            </button>
          ))}
        </div>

        {/* Age group tabs — shown for all views except pending */}
        {activeFilter !== "pending" && (
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="age-group-tabs">
            {AGE_GROUPS.map((ag) => {
              const counts = ageGroupCounts[ag];
              return (
                <button
                  key={ag}
                  onClick={() => setAgeTab(ag)}
                  data-testid={`age-tab-${ag.toLowerCase()}`}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    ageTab === ag
                      ? "bg-white text-[#1E2761] shadow-sm font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {ag}
                  {counts.teams > 0 && (
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                      ageTab === ag ? "bg-[#1E2761] text-white" : "bg-gray-200 text-gray-600"
                    }`}>
                      {counts.teams}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Sub-view: all academies — team cards, click to see stats */}
        {activeFilter === "all" && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {ageGroupCounts[ageTab].teams} teams · {ageGroupCounts[ageTab].players} players registered
              </p>
            </div>
            <AgeGroupRoster showPlayers={false} />
          </>
        )}

        {/* Sub-view: teams — same age-group cards */}
        {activeFilter === "teams" && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {ageGroupCounts[ageTab].teams} teams in {ageTab}
              </p>
            </div>
            <div className="space-y-2" data-testid="teams-list">
              {teamsForAge(ageTab).map((t) => (
                <div key={`${t.academyId}-${t.ageGroup}`} className="bg-white rounded-xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.academyName}</p>
                    <p className="text-xs text-gray-500">Coach: {t.coach} · {t.playerCount} players</p>
                  </div>
                  <Badge className={`border text-xs ${STATUS_BADGE[t.status]}`}>{t.status}</Badge>
                </div>
              ))}
              {teamsForAge(ageTab).length === 0 && (
                <p className="text-sm text-gray-400 italic text-center py-8">No teams registered for {ageTab}.</p>
              )}
            </div>
          </>
        )}

        {/* Sub-view: players — age-group tabs + roster grid */}
        {activeFilter === "players" && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {ageGroupCounts[ageTab].players} players across {ageGroupCounts[ageTab].teams} teams · click a team to expand
              </p>
            </div>
            <AgeGroupRoster showPlayers={true} />
          </>
        )}

        {/* Sub-view: pending */}
        {activeFilter === "pending" && (
          <div className="space-y-2" data-testid="pending-list">
            {pendingTeams.length === 0 ? (
              <p className="text-sm text-gray-400 italic text-center py-8">No pending teams.</p>
            ) : (
              pendingTeams.map((t, i) => (
                <div key={i} className="bg-amber-50 rounded-xl border border-amber-200 px-5 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.academyName} — {t.ageGroup}</p>
                    <p className="text-xs text-gray-500">{t.playerCount} players awaiting registration confirmation</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => toast({ title: "Reminder sent", description: `Registration reminder sent to ${t.academyName}.` })}
                  >
                    Send Reminder
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Import Roster Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Import Roster</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-500">Upload a CSV or Excel file to bulk-import player registrations.</p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="text-sm text-gray-700 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:text-xs file:bg-gray-50 file:cursor-pointer"
                onChange={(e) => setUploadFile(e.target.files?.[0]?.name ?? null)}
              />
              {uploadFile && <p className="text-xs text-gray-500">Selected: {uploadFile}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" size="sm" onClick={() => { setImportOpen(false); setUploadFile(null); }}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                  disabled={!uploadFile}
                  onClick={handleImport}
                >
                  Upload
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </HubAdminDemoLayout>
  );
}
